// Browser-side WayFinder handoff helpers for Case Buddy Web App.
// This module intentionally models the desktop queue shape without assuming
// the browser can write the local SQLite queue directly.

import {
  normalizeCell,
  normalizeComparisonKey
} from './CaseBuddyShared.js';


export const CASE_WAYFINDER_HANDOFF_COLUMNS = Object.freeze([
  'Path Label',
  'Transaction Hash',
  'From Address',
  'To Address',
  'UTC Date',
  'Amount',
  'TokenSymbol',
  '',
  '',
  'System Notes',
  'Attribution',
  'Analyst Notes',
  'Funds Followed',
  'Prior Balance',
  'From Hash',
  '',
  'Continues As',
  'Left Bounds Display',
  'Left Bounds Numeric',
  'Chain Id',
  'Chain Name',
  'Hop Logic'
]);

export const CASE_WAYFINDER_HANDOFF_SCHEMA = 'casebuddy_wayfinder_handoff_v1';
export const CASE_WAYFINDER_HANDOFF_STATE_VERSION = 1;
export const CASE_WAYFINDER_HANDOFF_MODE = 'casebuddy_raw_fetch';
export const CASE_WAYFINDER_HANDOFF_DEFAULT_BRIDGE_URL = 'http://127.0.0.1:8787/api';
export const CASE_WAYFINDER_HANDOFF_LEASE_SECONDS = 300;

export function createEmptyWayFinderHandoffState() {
  return {
    version: CASE_WAYFINDER_HANDOFF_STATE_VERSION,
    bridgeUrl: CASE_WAYFINDER_HANDOFF_DEFAULT_BRIDGE_URL,
    jobs: [],
    lastExportedUtc: '',
    lastSentUtc: '',
    lastImportedUtc: '',
    lastDismissedUtc: '',
    lastStatusMessage: ''
  };
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function utcNow() {
  return new Date().toISOString();
}

function safeJsonParse(value, fallback = {}) {
  if (!value || typeof value !== 'string') return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

function stringifyMetadata(value = {}) {
  if (typeof value === 'string') return value.trim() || '{}';
  try {
    return JSON.stringify(value || {});
  } catch (error) {
    return '{}';
  }
}

function firstString(...values) {
  for (const value of values) {
    const text = normalizeCell(value);
    if (text) return text;
  }
  return '';
}

function asInteger(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : fallback;
}

function normalizeHandoffStatus(value, fallback = 'queued') {
  const text = normalizeCell(value).toLowerCase().replace(/[\s-]+/g, '_');
  if (['queued', 'sent', 'in_progress', 'completed', 'failed', 'dismissed', 'imported', 'cancelled'].includes(text)) {
    return text === 'cancelled' ? 'dismissed' : text;
  }
  if (text === 'complete') return 'completed';
  if (text === 'pending') return 'queued';
  return fallback;
}

function normalizeImportStatus(value, fallback = 'pending') {
  const text = normalizeCell(value).toLowerCase().replace(/[\s-]+/g, '_');
  if (['pending', 'imported', 'failed', 'dismissed'].includes(text)) return text;
  return fallback;
}

function createJobId(caseId, requestedMainRowId, hash, toAddress, createdUtc = utcNow()) {
  const base = [
    'cbweb',
    normalizeComparisonKey(caseId) || 'case',
    normalizeComparisonKey(requestedMainRowId) || 'row',
    normalizeComparisonKey(hash).slice(0, 18) || 'hash',
    normalizeComparisonKey(toAddress).slice(0, 18) || 'any',
    Date.parse(createdUtc) || Date.now(),
    Math.random().toString(36).slice(2, 8)
  ].join('-');
  return base.replace(/[^a-z0-9_-]+/gi, '-').replace(/-+/g, '-').slice(0, 160);
}

export function getWayFinderHandoffJobKey(job = {}) {
  const caseId = normalizeComparisonKey(job.caseId || job.case_id);
  const requestedMainRowId = normalizeComparisonKey(job.requestedMainRowId || job.requested_main_row_id);
  const hash = normalizeComparisonKey(job.transactionHash || job.transaction_hash);
  const toAddress = normalizeComparisonKey(job.toAddress || job.to_address);
  return [caseId, requestedMainRowId, hash, toAddress || 'any-to'].join('|');
}

export function isWayFinderHandoffJobActive(job = {}) {
  const status = normalizeHandoffStatus(job.jobStatus || job.job_status, 'queued');
  const importStatus = normalizeImportStatus(job.caseBuddyImportStatus || job.case_buddy_import_status, 'pending');
  if (status === 'dismissed' || importStatus === 'dismissed') return false;
  if (status === 'failed') return false;
  if (status === 'imported' || importStatus === 'imported') return false;
  return true;
}

export function isWayFinderHandoffJobStale(job = {}, nowMs = Date.now()) {
  const status = normalizeHandoffStatus(job.jobStatus || job.job_status, 'queued');
  if (status !== 'in_progress') return false;
  const leaseText = normalizeCell(job.leaseExpiresUtc || job.lease_expires_utc);
  const updatedText = normalizeCell(job.updatedUtc || job.updated_utc);
  const leaseMs = leaseText ? Date.parse(leaseText) : NaN;
  if (Number.isFinite(leaseMs)) return leaseMs < nowMs;
  const updatedMs = updatedText ? Date.parse(updatedText) : NaN;
  if (Number.isFinite(updatedMs)) return nowMs - updatedMs > CASE_WAYFINDER_HANDOFF_LEASE_SECONDS * 1000;
  return true;
}

function formatWayFinderJobStatusLabel(job = {}) {
  const status = normalizeHandoffStatus(job.jobStatus || job.job_status, 'queued');
  const importStatus = normalizeImportStatus(job.caseBuddyImportStatus || job.case_buddy_import_status, 'pending');
  if (importStatus === 'imported' || status === 'imported') return 'Imported';
  if (status === 'in_progress') return isWayFinderHandoffJobStale(job) ? 'In progress · stale' : 'In progress';
  if (status === 'completed') return 'Ready to import';
  if (status === 'failed') return 'Failed';
  if (status === 'dismissed') return 'Dismissed';
  if (status === 'sent') return 'Sent';
  return 'Queued';
}

export function normalizeWayFinderHandoffJob(rawJob = {}) {
  const createdUtc = firstString(rawJob.createdUtc, rawJob.created_utc) || utcNow();
  const metadata = rawJob.metadata && typeof rawJob.metadata === 'object'
    ? rawJob.metadata
    : safeJsonParse(rawJob.metadataJson || rawJob.metadata_json, {});
  const transactionHash = firstString(rawJob.transactionHash, rawJob.transaction_hash, rawJob.hash);
  const toAddress = firstString(rawJob.toAddress, rawJob.to_address);
  const requestedMainRowId = firstString(rawJob.requestedMainRowId, rawJob.requested_main_row_id, metadata.requestedMainRowId, metadata.pathLabel, metadata.mainSheetRowNumber);
  const caseId = firstString(rawJob.caseId, rawJob.case_id, metadata.caseId);
  const jobStatus = normalizeHandoffStatus(rawJob.jobStatus || rawJob.job_status, 'queued');
  const caseBuddyImportStatus = normalizeImportStatus(rawJob.caseBuddyImportStatus || rawJob.case_buddy_import_status, 'pending');
  const jobId = firstString(rawJob.jobId, rawJob.job_id) || createJobId(caseId, requestedMainRowId, transactionHash, toAddress, createdUtc);

  return {
    jobId,
    caseId,
    requestedMainRowId,
    transactionHash,
    toAddress,
    requestType: firstString(rawJob.requestType, rawJob.request_type) || 'single',
    mode: firstString(rawJob.mode) || CASE_WAYFINDER_HANDOFF_MODE,
    hopLogic: firstString(rawJob.hopLogic, rawJob.hop_logic, metadata.hopLogic),
    jobStatus,
    caseBuddyImportStatus,
    workerId: firstString(rawJob.workerId, rawJob.worker_id),
    leaseExpiresUtc: firstString(rawJob.leaseExpiresUtc, rawJob.lease_expires_utc),
    attemptCount: asInteger(rawJob.attemptCount ?? rawJob.attempt_count, 0),
    resultTsv: '',
    resultRowCount: asInteger(rawJob.resultRowCount ?? rawJob.result_row_count, 0),
    workerErrorText: firstString(rawJob.workerErrorText, rawJob.worker_error_text),
    importErrorText: firstString(rawJob.importErrorText, rawJob.import_error_text),
    metadata,
    metadataJson: stringifyMetadata(metadata),
    createdUtc,
    updatedUtc: firstString(rawJob.updatedUtc, rawJob.updated_utc) || createdUtc,
    completedUtc: firstString(rawJob.completedUtc, rawJob.completed_utc),
    importedUtc: firstString(rawJob.importedUtc, rawJob.imported_utc),
    dismissedUtc: firstString(rawJob.dismissedUtc, rawJob.dismissed_utc),
    targetRowIndex: asInteger(rawJob.targetRowIndex ?? rawJob.target_row_index ?? metadata.targetRowIndex, -1),
    targetSpreadsheetRowNumber: asInteger(rawJob.targetSpreadsheetRowNumber ?? rawJob.target_spreadsheet_row_number ?? metadata.mainSheetRowNumber, 0),
    pathLabel: firstString(rawJob.pathLabel, rawJob.path_label, metadata.pathLabel),
    source: firstString(rawJob.source) || 'case_buddy_web_app'
  };
}

export function normalizeWayFinderHandoffState(rawState = {}) {
  const defaults = createEmptyWayFinderHandoffState();
  const source = rawState && typeof rawState === 'object' ? rawState : {};
  const jobs = asArray(source.jobs || source.handoffJobs || source.handoff_jobs)
    .map(normalizeWayFinderHandoffJob)
    .filter(job => job.jobId && job.transactionHash)
    .slice(-500);

  return {
    ...defaults,
    ...source,
    version: CASE_WAYFINDER_HANDOFF_STATE_VERSION,
    bridgeUrl: firstString(source.bridgeUrl, source.bridge_url) || defaults.bridgeUrl,
    jobs,
    lastExportedUtc: firstString(source.lastExportedUtc, source.last_exported_utc),
    lastSentUtc: firstString(source.lastSentUtc, source.last_sent_utc),
    lastImportedUtc: firstString(source.lastImportedUtc, source.last_imported_utc),
    lastDismissedUtc: firstString(source.lastDismissedUtc, source.last_dismissed_utc),
    lastStatusMessage: firstString(source.lastStatusMessage, source.last_status_message)
  };
}

export function createWayFinderHandoffStateForStorage(rawState = {}) {
  const state = normalizeWayFinderHandoffState(rawState);
  return {
    ...state,
    jobs: state.jobs.map(job => ({
      ...job,
      resultTsv: ''
    }))
  };
}

export function buildWayFinderRawHandoffRequests({ missingRows = [], caseId = '', caseName = '', caseRevision = 0 } = {}) {
  const now = utcNow();
  return asArray(missingRows)
    .map((row, index) => {
      const transactionHash = normalizeCell(row.hash || row.transactionHash || row.transaction_hash);
      if (!transactionHash) return null;
      const targetSpreadsheetRowNumber = asInteger(row.spreadsheetRowNumber, asInteger(row.rowIndex, -1) + 1);
      const pathLabel = normalizeCell(row.pathLabel) || (targetSpreadsheetRowNumber > 0 ? `Row ${targetSpreadsheetRowNumber}` : `Target ${index + 1}`);
      const requestedMainRowId = `main-row-${targetSpreadsheetRowNumber || index + 1}-${normalizeComparisonKey(pathLabel) || 'unlabeled'}`;
      const toAddress = normalizeCell(row.toAddress);
      const hopLogic = normalizeCell(row.hopLogic || row.hop_logic || row.row?.[21]);
      const metadata = {
        schema: CASE_WAYFINDER_HANDOFF_SCHEMA,
        source: 'case_buddy_web_app',
        caseId,
        caseName,
        caseRevision,
        pathLabel,
        mainSheetRowNumber: targetSpreadsheetRowNumber,
        targetRowIndex: asInteger(row.rowIndex, targetSpreadsheetRowNumber > 0 ? targetSpreadsheetRowNumber - 1 : -1),
        requiresToAddressMatch: !!normalizeComparisonKey(toAddress),
        coverageStatus: normalizeCell(row.status || row.statusLabel),
        coverageMessage: normalizeCell(row.message),
        shortHash: normalizeCell(row.shortHash),
        shortToAddress: normalizeCell(row.shortToAddress),
        hopLogic
      };

      return normalizeWayFinderHandoffJob({
        jobId: createJobId(caseId, requestedMainRowId, transactionHash, toAddress, now),
        caseId,
        requestedMainRowId,
        transactionHash,
        toAddress,
        requestType: 'single',
        mode: CASE_WAYFINDER_HANDOFF_MODE,
        hopLogic,
        jobStatus: 'queued',
        caseBuddyImportStatus: 'pending',
        attemptCount: 0,
        metadata,
        createdUtc: now,
        updatedUtc: now,
        targetRowIndex: metadata.targetRowIndex,
        targetSpreadsheetRowNumber,
        pathLabel
      });
    })
    .filter(Boolean);
}

export function mergeWayFinderHandoffJobs(existingJobs = [], incomingJobs = [], { replaceActive = false } = {}) {
  const byKey = new Map();
  const now = utcNow();
  asArray(existingJobs).map(normalizeWayFinderHandoffJob).forEach((job) => {
    byKey.set(getWayFinderHandoffJobKey(job), job);
  });

  let addedCount = 0;
  let skippedActiveCount = 0;
  let refreshedCount = 0;

  asArray(incomingJobs).map(normalizeWayFinderHandoffJob).forEach((job) => {
    const key = getWayFinderHandoffJobKey(job);
    const existing = byKey.get(key);
    if (existing && isWayFinderHandoffJobActive(existing) && !replaceActive) {
      skippedActiveCount += 1;
      return;
    }
    if (existing) {
      refreshedCount += 1;
    } else {
      addedCount += 1;
    }
    byKey.set(key, {
      ...(existing || {}),
      ...job,
      jobId: existing?.jobId && !replaceActive ? existing.jobId : job.jobId,
      jobStatus: 'queued',
      caseBuddyImportStatus: 'pending',
      dismissedUtc: '',
      updatedUtc: now
    });
  });

  return {
    jobs: Array.from(byKey.values()).sort((left, right) => {
      const leftRow = asInteger(left.targetSpreadsheetRowNumber, 0);
      const rightRow = asInteger(right.targetSpreadsheetRowNumber, 0);
      if (leftRow !== rightRow) return leftRow - rightRow;
      return String(left.createdUtc || '').localeCompare(String(right.createdUtc || ''));
    }),
    addedCount,
    skippedActiveCount,
    refreshedCount
  };
}

function toExportJob(job = {}) {
  const normalized = normalizeWayFinderHandoffJob(job);
  return {
    job_id: normalized.jobId,
    case_id: normalized.caseId,
    requested_main_row_id: normalized.requestedMainRowId,
    transaction_hash: normalized.transactionHash,
    to_address: normalized.toAddress,
    request_type: normalized.requestType || 'single',
    mode: normalized.mode || CASE_WAYFINDER_HANDOFF_MODE,
    hop_logic: normalized.hopLogic || '',
    job_status: normalizeHandoffStatus(normalized.jobStatus, 'queued') === 'sent' ? 'queued' : normalizeHandoffStatus(normalized.jobStatus, 'queued'),
    case_buddy_import_status: normalized.caseBuddyImportStatus || 'pending',
    worker_id: normalized.workerId || '',
    lease_expires_utc: normalized.leaseExpiresUtc || '',
    attempt_count: normalized.attemptCount || 0,
    result_tsv: '',
    worker_error_text: normalized.workerErrorText || '',
    import_error_text: normalized.importErrorText || '',
    metadata_json: normalized.metadataJson || stringifyMetadata(normalized.metadata || {}),
    created_utc: normalized.createdUtc || utcNow(),
    updated_utc: normalized.updatedUtc || normalized.createdUtc || utcNow(),
    completed_utc: normalized.completedUtc || '',
    imported_utc: normalized.importedUtc || ''
  };
}

export function buildWayFinderHandoffRequestPackage({ jobs = [], caseId = '', caseName = '', caseRevision = 0, includeDismissed = false } = {}) {
  const exportJobs = asArray(jobs)
    .map(normalizeWayFinderHandoffJob)
    .filter(job => job.transactionHash && (includeDismissed || isWayFinderHandoffJobActive(job)))
    .filter(job => ['queued', 'sent', 'in_progress'].includes(normalizeHandoffStatus(job.jobStatus, 'queued')))
    .map(toExportJob);
  const now = utcNow();

  return {
    schema: CASE_WAYFINDER_HANDOFF_SCHEMA,
    version: 1,
    source: 'case_buddy_web_app',
    transport: 'browser_package_or_local_bridge',
    created_utc: now,
    case_id: normalizeCell(caseId),
    case_name: normalizeCell(caseName),
    case_revision: asInteger(caseRevision, 0),
    notes: 'Import this package into the WayFinder Case Buddy handoff bridge. Results should be returned with completed jobs and result_tsv values.',
    columns: CASE_WAYFINDER_HANDOFF_COLUMNS,
    jobs: exportJobs
  };
}

export function stringifyWayFinderHandoffPackage(pkg = {}) {
  return JSON.stringify(pkg || {}, null, 2);
}

function normalizeCompletedResultJob(rawJob = {}) {
  const normalized = normalizeWayFinderHandoffJob(rawJob);
  const resultTsv = firstString(rawJob.resultTsv, rawJob.result_tsv, rawJob.result, rawJob.tsv, rawJob.rawTsv, rawJob.raw_tsv, rawJob.fullResults, rawJob.full_results);
  return {
    ...normalized,
    resultTsv,
    resultRowCount: resultTsv ? resultTsv.split(/\r?\n/).filter(line => line.trim()).length : normalized.resultRowCount,
    jobStatus: resultTsv ? 'completed' : normalized.jobStatus,
    completedUtc: normalized.completedUtc || (resultTsv ? utcNow() : ''),
    caseBuddyImportStatus: normalized.caseBuddyImportStatus || 'pending'
  };
}

export function parseWayFinderHandoffResultPayload(text = '') {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return { ok: false, message: 'Paste a completed WayFinder handoff result package first.', jobs: [], combinedTsv: '' };
  }

  let parsed = null;
  if (rawText.startsWith('{') || rawText.startsWith('[')) {
    try {
      parsed = JSON.parse(rawText);
    } catch (error) {
      return { ok: false, message: `The pasted JSON could not be parsed: ${error?.message || error}`, jobs: [], combinedTsv: '' };
    }
  }

  if (!parsed) {
    return {
      ok: true,
      message: 'Plain TSV detected. It can be appended as raw data, but no handoff job status can be updated from it.',
      jobs: [],
      combinedTsv: rawText,
      plainTsv: true
    };
  }

  const rawJobs = Array.isArray(parsed)
    ? parsed
    : asArray(parsed.jobs || parsed.handoff_jobs || parsed.completedJobs || parsed.completed_jobs || parsed.results);

  const jobs = rawJobs.map(normalizeCompletedResultJob).filter(job => job.resultTsv);
  if (jobs.length === 0) {
    const directTsv = firstString(parsed.resultTsv, parsed.result_tsv, parsed.tsv, parsed.rawTsv, parsed.raw_tsv);
    if (directTsv) {
      return {
        ok: true,
        message: 'One direct result TSV was found in the package.',
        jobs: [normalizeCompletedResultJob({ ...parsed, resultTsv: directTsv })],
        combinedTsv: directTsv
      };
    }
    return { ok: false, message: 'No completed jobs with result_tsv values were found in the package.', jobs: [], combinedTsv: '' };
  }

  const combinedTsv = jobs.map(job => job.resultTsv.trim()).filter(Boolean).join('\n\n');
  return {
    ok: true,
    message: `${jobs.length} completed handoff result${jobs.length === 1 ? '' : 's'} found.`,
    jobs,
    combinedTsv
  };
}

export function applyWayFinderHandoffImportedResultsToState(rawState = {}, importedJobs = []) {
  const state = normalizeWayFinderHandoffState(rawState);
  const now = utcNow();
  const importedById = new Map();
  const importedByKey = new Map();
  asArray(importedJobs).map(normalizeWayFinderHandoffJob).forEach((job) => {
    if (job.jobId) importedById.set(job.jobId, job);
    importedByKey.set(getWayFinderHandoffJobKey(job), job);
  });

  let matchedCount = 0;
  const jobs = state.jobs.map((job) => {
    const match = importedById.get(job.jobId) || importedByKey.get(getWayFinderHandoffJobKey(job));
    if (!match) return job;
    matchedCount += 1;
    return {
      ...job,
      jobStatus: 'imported',
      caseBuddyImportStatus: 'imported',
      completedUtc: match.completedUtc || job.completedUtc || now,
      importedUtc: now,
      resultRowCount: match.resultRowCount || job.resultRowCount || 0,
      workerId: match.workerId || job.workerId,
      updatedUtc: now
    };
  });

  return {
    ...state,
    jobs,
    lastImportedUtc: now,
    lastStatusMessage: matchedCount > 0
      ? `Imported ${matchedCount} completed WayFinder handoff result${matchedCount === 1 ? '' : 's'}.`
      : state.lastStatusMessage
  };
}

export function dismissWayFinderHandoffJobs(rawState = {}, predicate = () => false) {
  const state = normalizeWayFinderHandoffState(rawState);
  const now = utcNow();
  let dismissedCount = 0;
  const jobs = state.jobs.map((job) => {
    if (!predicate(job)) return job;
    dismissedCount += 1;
    return {
      ...job,
      jobStatus: 'dismissed',
      caseBuddyImportStatus: job.caseBuddyImportStatus === 'imported' ? 'imported' : 'dismissed',
      dismissedUtc: now,
      updatedUtc: now
    };
  });
  return {
    ...state,
    jobs,
    lastDismissedUtc: dismissedCount > 0 ? now : state.lastDismissedUtc,
    lastStatusMessage: dismissedCount > 0 ? `Dismissed ${dismissedCount} WayFinder handoff request${dismissedCount === 1 ? '' : 's'}.` : state.lastStatusMessage,
    dismissedCount
  };
}

export function requeueStaleWayFinderHandoffJobs(rawState = {}) {
  const state = normalizeWayFinderHandoffState(rawState);
  const now = utcNow();
  let requeuedCount = 0;
  const jobs = state.jobs.map((job) => {
    if (!isWayFinderHandoffJobStale(job)) return job;
    requeuedCount += 1;
    return {
      ...job,
      jobStatus: 'queued',
      workerId: '',
      leaseExpiresUtc: '',
      updatedUtc: now,
      workerErrorText: ''
    };
  });
  return {
    ...state,
    jobs,
    lastStatusMessage: requeuedCount > 0 ? `Requeued ${requeuedCount} stale in-progress request${requeuedCount === 1 ? '' : 's'}.` : state.lastStatusMessage,
    requeuedCount
  };
}

export function summarizeWayFinderHandoffState(rawState = {}) {
  const state = normalizeWayFinderHandoffState(rawState);
  const counts = {
    queued: 0,
    sent: 0,
    inProgress: 0,
    staleInProgress: 0,
    completed: 0,
    imported: 0,
    failed: 0,
    dismissed: 0,
    active: 0,
    total: state.jobs.length
  };

  state.jobs.forEach((job) => {
    const status = normalizeHandoffStatus(job.jobStatus, 'queued');
    if (status === 'queued') counts.queued += 1;
    if (status === 'sent') counts.sent += 1;
    if (status === 'in_progress') counts.inProgress += 1;
    if (status === 'completed') counts.completed += 1;
    if (status === 'imported' || job.caseBuddyImportStatus === 'imported') counts.imported += 1;
    if (status === 'failed') counts.failed += 1;
    if (status === 'dismissed' || job.caseBuddyImportStatus === 'dismissed') counts.dismissed += 1;
    if (isWayFinderHandoffJobStale(job)) counts.staleInProgress += 1;
    if (isWayFinderHandoffJobActive(job)) counts.active += 1;
  });

  return counts;
}

export const CASE_WAYFINDER_HANDOFF_SETTINGS_KEY = 'casebuddy_wayfinder_handoff_settings_v1';
export const CASE_WAYFINDER_HANDOFF_POLL_INTERVAL_MS = 8000;
export const CASE_WAYFINDER_HANDOFF_STALE_MINUTES = 30;

export function normalizeWayFinderBridgeUrl(value = '') {
  const text = normalizeCell(value) || 'http://127.0.0.1:8787/api';
  return text.replace(/\/+$/, '');
}

export function buildWayFinderHandoffEndpoint(baseUrl = '', path = '') {
  const base = normalizeWayFinderBridgeUrl(baseUrl || 'http://127.0.0.1:8787/api');
  const cleanPath = String(path || '').replace(/^\/+/, '');
  return `${base}/${cleanPath}`;
}

export function buildWayFinderHandoffCaseId({ currentProjectFileId = '', caseDisplayId = '', caseDisplayName = '' } = {}) {
  const displayId = normalizeCell(caseDisplayId);
  if (displayId && !/^no case id set$/i.test(displayId)) return displayId;
  return normalizeCell(currentProjectFileId) || normalizeComparisonKey(caseDisplayName) || 'case';
}

export function buildWayFinderHandoffTargetsFromCoverage(coverage = {}, { mainData = [], caseId = '', caseName = '', caseRevision = 0 } = {}) {
  const rows = asArray(coverage.missingRows || coverage.rows || coverage.coverageRows)
    .filter(row => row && row.status !== 'covered')
    .map((row, index) => {
      const transactionHash = normalizeCell(row.hash || row.transactionHash);
      const toAddress = normalizeCell(row.toAddress);
      const spreadsheetRowNumber = asInteger(row.spreadsheetRowNumber, asInteger(row.rowIndex, index) + 1);
      const pathLabel = normalizeCell(row.pathLabel) || `Row ${spreadsheetRowNumber}`;
      const sourceRow = Array.isArray(mainData?.[spreadsheetRowNumber - 1]) ? mainData[spreadsheetRowNumber - 1] : [];
      const hopLogic = normalizeCell(row.hopLogic || sourceRow[21]);
      const chainId = normalizeCell(row.chainId || sourceRow[19]);
      const chainName = normalizeCell(row.chainName || sourceRow[20]);
      const requestedMainRowId = `main-row-${spreadsheetRowNumber}-${normalizeComparisonKey(pathLabel) || index + 1}`;
      const id = [caseId, requestedMainRowId, transactionHash, toAddress || 'any-to'].map(normalizeComparisonKey).join('|');
      return {
        id,
        caseId,
        caseName,
        caseRevision,
        requestedMainRowId,
        transactionHash,
        hash: transactionHash,
        toAddress,
        pathLabel,
        spreadsheetRowNumber,
        rowIndex: asInteger(row.rowIndex, spreadsheetRowNumber - 1),
        chainId,
        chainName,
        hopLogic,
        status: row.status || 'missing',
        statusLabel: normalizeCell(row.statusLabel) || 'Missing raw data',
        message: normalizeCell(row.message),
        canQueue: !!transactionHash,
        queuedJob: null,
        queueStatus: '',
        jobStatus: '',
        importStatus: ''
      };
    });
  return rows;
}

export function buildWayFinderHandoffJobPayload(target = {}) {
  const now = utcNow();
  const metadata = {
    schema: CASE_WAYFINDER_HANDOFF_SCHEMA,
    source: 'case_buddy_web_app',
    caseId: normalizeCell(target.caseId),
    caseName: normalizeCell(target.caseName),
    caseRevision: asInteger(target.caseRevision, 0),
    pathLabel: normalizeCell(target.pathLabel),
    mainSheetRowNumber: asInteger(target.spreadsheetRowNumber, 0),
    targetRowIndex: asInteger(target.rowIndex, -1),
    chainId: normalizeCell(target.chainId),
    chainName: normalizeCell(target.chainName),
    hopLogic: normalizeCell(target.hopLogic),
    coverageStatus: normalizeCell(target.statusLabel || target.status),
    coverageMessage: normalizeCell(target.message)
  };
  const job = normalizeWayFinderHandoffJob({
    caseId: target.caseId,
    requestedMainRowId: target.requestedMainRowId,
    transactionHash: target.transactionHash || target.hash,
    toAddress: target.toAddress,
    requestType: 'single',
    mode: CASE_WAYFINDER_HANDOFF_MODE,
    hopLogic: target.hopLogic,
    jobStatus: 'queued',
    caseBuddyImportStatus: 'pending',
    metadata,
    createdUtc: now,
    updatedUtc: now,
    targetSpreadsheetRowNumber: target.spreadsheetRowNumber,
    targetRowIndex: target.rowIndex,
    pathLabel: target.pathLabel
  });
  return toExportJob(job);
}

function normalizeBridgeJob(rawJob = {}) {
  const job = normalizeWayFinderHandoffJob(rawJob);
  const resultTsv = firstString(rawJob.resultTsv, rawJob.result_tsv, rawJob.result, rawJob.tsv, rawJob.rawTsv, rawJob.raw_tsv);
  const status = normalizeHandoffStatus(rawJob.jobStatus || rawJob.job_status || job.jobStatus, 'queued');
  const importStatus = normalizeImportStatus(rawJob.caseBuddyImportStatus || rawJob.case_buddy_import_status || job.caseBuddyImportStatus, 'pending');
  return {
    ...job,
    jobStatus: status,
    caseBuddyImportStatus: importStatus,
    resultTsv,
    resultRowCount: resultTsv ? resultTsv.split(/\r?\n/).filter(line => line.trim()).length : job.resultRowCount,
    canImport: status === 'completed' && importStatus !== 'imported' && !!resultTsv,
    isStaleInProgress: isWayFinderHandoffJobStale({ ...job, jobStatus: status, caseBuddyImportStatus: importStatus }),
    statusLabel: formatWayFinderJobStatusLabel({ ...job, jobStatus: status, caseBuddyImportStatus: importStatus })
  };
}

export function normalizeWayFinderHandoffJobs(payload = {}) {
  const rawJobs = Array.isArray(payload)
    ? payload
    : asArray(payload.jobs || payload.handoff_jobs || payload.results || payload.data);
  return rawJobs.map(normalizeBridgeJob).filter(job => job.jobId || job.transactionHash);
}

export function summarizeWayFinderHandoffJobs(jobs = []) {
  return asArray(jobs).reduce((summary, job) => {
    const normalized = normalizeBridgeJob(job);
    summary.total += 1;
    if (normalized.jobStatus === 'queued') summary.queued += 1;
    else if (normalized.jobStatus === 'in_progress') summary.inProgress += 1;
    else if (normalized.jobStatus === 'completed' && normalized.caseBuddyImportStatus === 'imported') summary.imported += 1;
    else if (normalized.jobStatus === 'completed') summary.completed += 1;
    else if (normalized.jobStatus === 'failed') summary.failed += 1;
    else if (normalized.jobStatus === 'dismissed') summary.dismissed += 1;
    else summary.other += 1;
    if (normalized.canImport) summary.readyToImport += 1;
    if (normalized.isStaleInProgress) summary.staleInProgress += 1;
    return summary;
  }, { total: 0, queued: 0, inProgress: 0, completed: 0, readyToImport: 0, imported: 0, failed: 0, dismissed: 0, staleInProgress: 0, other: 0 });
}

export function mergeWayFinderHandoffJobsIntoTargets(targets = [], jobs = []) {
  const normalizedJobs = asArray(jobs).map(normalizeBridgeJob);
  const byKey = new Map();
  normalizedJobs.forEach((job) => {
    const keys = [
      [job.caseId, job.requestedMainRowId, job.transactionHash, job.toAddress || 'any-to'].map(normalizeComparisonKey).join('|'),
      [job.caseId, job.requestedMainRowId, job.transactionHash, 'any-to'].map(normalizeComparisonKey).join('|'),
      [job.caseId, '', job.transactionHash, job.toAddress || 'any-to'].map(normalizeComparisonKey).join('|'),
      [job.caseId, '', job.transactionHash, 'any-to'].map(normalizeComparisonKey).join('|')
    ];
    keys.forEach((key) => {
      if (key && !byKey.has(key)) byKey.set(key, job);
    });
  });

  return asArray(targets).map((target) => {
    const keys = [
      [target.caseId, target.requestedMainRowId, target.transactionHash || target.hash, target.toAddress || 'any-to'].map(normalizeComparisonKey).join('|'),
      [target.caseId, target.requestedMainRowId, target.transactionHash || target.hash, 'any-to'].map(normalizeComparisonKey).join('|'),
      [target.caseId, '', target.transactionHash || target.hash, target.toAddress || 'any-to'].map(normalizeComparisonKey).join('|'),
      [target.caseId, '', target.transactionHash || target.hash, 'any-to'].map(normalizeComparisonKey).join('|')
    ];
    const job = keys.map(key => byKey.get(key)).find(Boolean) || null;
    const handoffStatusLabel = job ? formatWayFinderJobStatusLabel(job) : (target.statusLabel || 'Missing raw data');
    return {
      ...target,
      queuedJob: job,
      latestJob: job,
      handoffStatusLabel,
      queueStatus: job ? job.jobStatus : '',
      jobStatus: job ? job.jobStatus : '',
      importStatus: job ? job.caseBuddyImportStatus : '',
      canQueue: !!target.canQueue && (!job || ['failed', 'dismissed'].includes(job.jobStatus) || job.caseBuddyImportStatus === 'imported'),
      canDismissJob: !!job && !['imported', 'dismissed'].includes(job.jobStatus) && job.caseBuddyImportStatus !== 'imported'
    };
  });
}
